import {
  Body,
  Button,
  H3,
  MaterialIcon,
  Stack,
} from '@qred/components-library';
import React from 'react';
import { useSelector } from 'react-redux';
import BankConnection from '~/components/shared/BankConnection/BankConnection';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import useTranslate from '~/hooks/useTranslate';
import { RootState } from '~/store';
import { patchApplication } from '~/store/actions/loanApplicationActions';
import {
  setBankConnectionError,
  updateForm,
} from '~/store/slices/loanApplication.slice';
import {
  GoldenRatioContainer,
  StandardStepContent,
  StandardStepContentBody,
  StandardStepContentFooter,
} from '~/styles/BaseStyledComponents';
import { BankConnectionFooter } from './components/BankConnectionStepFooter';
import BankConnectionStepHeader from './components/BankConnectionStepHeader';

const BankConnectionStep = () => {
  const dispatch = useAppDispatch();
  const translate = useTranslate();
  const {
    loanApplication: {
      clientId,
      bankConnectionError,
      form: { bankConnected },
    },
  } = useSelector((state: RootState) => state);

  const onBeforeRedirect = async () => {
    await dispatch(patchApplication({ currentStepCompleted: false }));
  };

  if (!clientId) {
    throw new Error('Client ID is required for BankConnectionStep');
  }

  return (
    <StandardStepContent>
      <StandardStepContentBody
        style={
          bankConnected
            ? {
                backgroundImage: 'url(/swoosh.png)',
                backgroundRepeat: 'no-repeat',
                backgroundPositionY: '90%',
                backgroundSize: 'contain',
              }
            : undefined
        }
      >
        <BankConnectionStepHeader />
        {bankConnected ? (
          <GoldenRatioContainer>
            <Stack spacing="xxl" align="center">
              <MaterialIcon
                src="verified"
                filled
                size="48"
                color="Icon/Success"
              />
              <H3 data-record>
                {translate(
                  'Onboarding.BankConnectionSuccessfullyConnectedTitle'
                )}
              </H3>
              <Body size="lg" data-record>
                {translate(
                  'Onboarding.BankConnectionSuccessfullyConnectedDescription'
                )}
                {/* TODO: remove before final release */}
                {['development', 'test'].includes(import.meta.env.MODE) && (
                  <Button
                    onClick={() => {
                      dispatch(updateForm({ bankConnected: false }));
                      dispatch(
                        patchApplication({ currentStepCompleted: false })
                      );
                    }}
                    variant="tertiary"
                  >
                    Revert (for testing purposes)
                  </Button>
                )}
              </Body>
            </Stack>
          </GoldenRatioContainer>
        ) : (
          <BankConnection
            clientId={clientId}
            hasError={bankConnectionError}
            finalRedirectUrl={location.href}
            setError={(error) => dispatch(setBankConnectionError(error))}
            onBeforeRedirect={onBeforeRedirect}
          />
        )}
      </StandardStepContentBody>
      <StandardStepContentFooter>
        <BankConnectionFooter />
      </StandardStepContentFooter>
    </StandardStepContent>
  );
};

export default BankConnectionStep;
